import { getSegment } from "./segment/helpers.js";
import {
  ASSESSMENT_NAME,
  CELLS,
  CONFIRM_BY_CELL,
  DISCLAIMER,
  DOMAINS,
  DOMAIN_CANON,
  DOMAIN_OVERRIDES,
  INDUSTRY_NAMES,
  MATURITY_LABELS,
  SLOTS,
  SPINE,
  readinessTiers,
  dated,
} from "./lib/apra-assessment-data.js";

/*!
 * Readiness Assessment results viewer - PagerDuty WordPress build.
 * Build: 085c12d* 2026-09-29 13:00 UTC
 */
("use strict");
(function () {
  function segTrack(event, props) {
    getSegment("track")(event, props || {});
  }

  var HOME = {
    "au-fsi": {
      h1: "AI is adding pressure to APRA-regulated operations. How ready is your organisation?",
      sub: "A few minutes. Get a scorecard showing potential gaps across critical operations, incident response, breach reporting, evidence trails and emerging AI risk controls.",
      chips: ["AI readiness", "CPS 230", "CPS 234", "SOCI Act", "Privacy Act"],
    },
    "au-fsi-lite": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "A few minutes. AI governance expectations and privacy clocks don't stop at the prudential perimeter. Check your current position.",
      chips: [
        "AI readiness",
        "Licence obligations",
        "Breach reporting",
        "Privacy Act",
      ],
    },
    "au-telco": {
      h1: "How outage-ready is your network operation?",
      sub: dated(
        "2026-10-04",
        "A few minutes. Maturity indicator 1 falls due on 3 October 2026. The triple-zero rules carry penalties up to $30 million. And AI is now used within operational workflows. Check your current position.",
        "A few minutes. Maturity indicator 1 fell due on 3 October 2026 - where did you land? The triple-zero rules carry penalties up to $30 million. And AI is now used within operational workflows. Check your current position.",
      ),
      chips: [
        "SOCI / TSRMP",
        "ECS Rules",
        "Outage Comms Standard",
        "AI readiness",
      ],
    },
    "au-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning. See whether your response keeps up with your losses.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "Privacy Act",
      ],
    },
    "au-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. CPS 230 pushes your FSI customers' obligations into your contracts - and their regulators are now asking about AI suppliers. See whether your incident response stands up.",
      chips: [
        "CPS 230 flow-down",
        "Customer evidence (SOC 2 / ISO 27001)",
        "AI readiness",
        "Privacy Act",
      ],
    },
    "au-digital-lite": {
      h1: "Your platform may be critical infrastructure now. How incident-ready are you?",
      sub: "A few minutes. Data storage and processing is a SOCI sector - meet the critical-asset threshold and CIRMP obligations and incident reporting follow, and AI is now used within operational workflows. See whether your response stands up.",
      chips: [
        "SOCI (data storage & processing)",
        "Customer evidence",
        "AI readiness",
        "Privacy Act",
      ],
    },
    "au-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to your Privacy Act obligations.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "Privacy Act",
      ],
    },
    "sg-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "A few minutes. MAS expects notification within one hour of discovering an incident, and has set out AI model-risk expectations. Check your current position as the updated ORM guidelines land.",
      chips: [
        "AI readiness",
        "MAS TRM",
        "Incident notification (1-hour)",
        "PDPA",
      ],
    },
    "sg-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. The Cybersecurity Act gives CII owners two hours to report an incident - and AI is now used within operational workflows.",
      chips: [
        "Cybersecurity Act (CII)",
        "IMDA resilience codes",
        "AI readiness",
        "PDPA",
      ],
    },
    "sg-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. The draft Digital Infrastructure Bill proposes resilience and incident-reporting obligations for major data centre and cloud providers - and MAS outsourcing rules already flow through your FSI contracts.",
      chips: [
        "Digital Infrastructure Bill (draft)",
        "MAS outsourcing flow-down",
        "AI readiness",
        "PDPA",
      ],
    },
    "sg-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPA",
      ],
    },
    "sg-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to your PDPA obligations.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPA",
      ],
    },
    "nz-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "A few minutes. The Reserve Bank already asks you to self-assess against its cyber resilience guidance - and AI is making the questions harder. This is a structured way to do both.",
      chips: [
        "AI readiness",
        "RBNZ Cyber Resilience Guidance",
        "Incident reporting (72-hour)",
        "Privacy Act 2020",
      ],
    },
    "nz-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. When networks fail, 111 access and customer communication are the first questions asked - and AI is now used within operational workflows.",
      chips: ["TICSA", "111 Contact Code", "AI readiness", "Privacy Act 2020"],
    },
    "nz-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. The Reserve Bank's outsourcing policy writes prescribed terms into large banks' vendor contracts - and their regulators are asking about AI suppliers.",
      chips: [
        "Outsourcing flow-down (BS11)",
        "Customer evidence",
        "AI readiness",
        "Privacy Act 2020",
      ],
    },
    "nz-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "Privacy Act 2020",
      ],
    },
    "nz-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to your Privacy Act 2020 obligations.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "Privacy Act 2020",
      ],
    },
    "hk-fsi": {
      h1: dated(
        "2027-06-01",
        "The OR-2 deadline has passed. Could you evidence your resilience?",
        "OR-2 is business as usual now. Could you still evidence it?",
      ),
      sub: dated(
        "2027-06-01",
        "A few minutes. The HKMA required banks to be operationally resilient by 31 May 2026 - and Cap. 653 now puts 12-hour incident clocks on critical infrastructure. Check your current position.",
        "A few minutes. The HKMA expects authorised institutions to be operationally resilient under SPM OR-2 - and Cap. 653 puts 12-hour incident clocks on critical infrastructure. Check your current position.",
      ),
      chips: [
        "HKMA OR-2",
        "C-RAF / TM-E-1",
        "Cap. 653 (12-hour)",
        "PDPO",
        "AI readiness",
      ],
    },
    "hk-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. Communications is a designated sector under Cap. 653 - serious incidents reported within 12 hours, others within 48 - and AI is now used within operational workflows.",
      chips: ["Cap. 653 (CII)", "OFCA obligations", "AI readiness", "PDPO"],
    },
    "hk-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPO",
      ],
    },
    "hk-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. HKMA operational-resilience and outsourcing expectations flow through your bank customers' contracts - and Cap. 653 reaches designated digital infrastructure directly.",
      chips: [
        "HKMA outsourcing flow-down",
        "Customer evidence",
        "AI readiness",
        "PDPO",
      ],
    },
    "hk-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to Cap. 653 and your PDPO obligations.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPO",
      ],
    },
    "my-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "A few minutes. BNM's RMiT sets some of APAC's most prescriptive technology-risk rules, and the Cyber Security Act has critical entities notifying NACSA immediately, with particulars inside six hours.",
      chips: [
        "AI readiness",
        "BNM RMiT",
        "Cyber Security Act (6-hour)",
        "PDPA",
      ],
    },
    "my-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. Communications is a critical sector under the Cyber Security Act 2024 - incidents go to NACSA immediately, with particulars within six hours.",
      chips: [
        "Cyber Security Act (NCII)",
        "MCMC obligations",
        "AI readiness",
        "PDPA",
      ],
    },
    "my-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPA",
      ],
    },
    "my-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. Your enterprise customers' obligations flow down through contracts - and critical-sector customers under the Cyber Security Act expect incident evidence on a six-hour clock.",
      chips: ["NCII flow-down", "Customer evidence", "AI readiness", "PDPA"],
    },
    "my-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to your PDPA obligations.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPA",
      ],
    },
    "th-fsi": {
      h1: "AI is now inside your critical operations. Is your resilience keeping up?",
      sub: "A few minutes. The Bank of Thailand's IT risk guidelines set the supervisory bar, the Cybersecurity Act gives critical infrastructure 24 hours to report, and the PDPA runs a 72-hour breach clock.",
      chips: [
        "AI readiness",
        "BOT IT risk guidelines",
        "Cybersecurity Act (24-hour)",
        "PDPA",
      ],
    },
    "th-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. Telecommunications is critical infrastructure under the Cybersecurity Act - the initial incident report is due to the NCSA within 24 hours.",
      chips: [
        "Cybersecurity Act (CII)",
        "NBTC obligations",
        "AI readiness",
        "PDPA",
      ],
    },
    "th-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI readiness",
        "PDPA",
      ],
    },
    "th-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. Your enterprise customers answer to the Bank of Thailand and the NCSA - their obligations flow into your contracts.",
      chips: [
        "Customer evidence",
        "Cybersecurity Act flow-down",
        "AI readiness",
        "PDPA",
      ],
    },
    "th-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to your PDPA obligations.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "PDPA",
      ],
    },
    "kr-fsi": {
      h1: "AI is now law in Korea. Is your resilience keeping up?",
      sub: "A few minutes. The AI Basic Act took effect in January 2026, e-finance incidents carry their own reporting regime, and PIPA gives you 72 hours on a breach.",
      chips: [
        "AI Basic Act",
        "E-finance incident reporting",
        "PIPA (72-hour)",
        "AI readiness",
      ],
    },
    "kr-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. Korea wrote network-stability obligations into law after high-profile outages, and the AI Basic Act now reaches the operational loop too.",
      chips: [
        "Network stability obligations",
        "AI Basic Act",
        "PIPA (72-hour)",
        "AI readiness",
      ],
    },
    "kr-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and AI failures now need to be part of operational-response planning.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "AI Basic Act",
        "PIPA",
      ],
    },
    "kr-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. Your enterprise customers' obligations flow down through contracts - and with the AI Basic Act in force, the AI in your platform is part of what their auditors ask about.",
      chips: ["Customer evidence", "AI Basic Act", "PIPA", "AI readiness"],
    },
    "kr-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading, mapped to PIPA and the AI Basic Act.",
      chips: [
        "Operational resilience",
        "AI Basic Act",
        "24/7 coverage",
        "PIPA",
      ],
    },
    "other-fsi": {
      h1: "AI is now inside critical financial operations. Is your resilience keeping up?",
      sub: "A few minutes. The same test wherever you operate: know your critical operations, detect fast, notify on a clock, keep the evidence.",
      chips: [
        "Operational resilience",
        "Breach response",
        "Evidence & audit",
        "AI readiness",
      ],
    },
    "other-telco": {
      h1: "How outage-ready is your network operation?",
      sub: "A few minutes. The fix and the customer communication are judged separately - both on the clock.",
      chips: [
        "Service continuity",
        "Emergency calls",
        "Outage comms",
        "AI readiness",
      ],
    },
    "other-ecom": {
      h1: "How much does a bad hour cost you?",
      sub: "A few minutes. When checkout breaks at peak, every minute has a dollar figure - and the fault is often a payment provider.",
      chips: [
        "Peak readiness",
        "Third parties & payments",
        "Breach response",
        "AI readiness",
      ],
    },
    "other-digital": {
      h1: "Your customers answer to regulators. Now so do you.",
      sub: "A few minutes. Enterprise customers' obligations flow down through contracts, and their auditors want evidence of YOUR incident response.",
      chips: [
        "Customer evidence",
        "Contractual obligations",
        "Breach response",
        "AI readiness",
      ],
    },
    "other-generic": {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading.",
      chips: [
        "Operational resilience",
        "AI readiness",
        "24/7 coverage",
        "Evidence & automation",
      ],
    },
    default: {
      h1: "AI is becoming part of more operational systems. How incident-ready are you?",
      sub: "A few minutes. Operational resilience with an AI readiness reading.",
      chips: [
        "Detection & response",
        "AI readiness",
        "24/7 coverage",
        "Evidence & automation",
      ],
    },
  };

  var GEO_INDUSTRY_COPY = {
    au: "Australia's rules are moving fast: operational risk standards for financial services, new obligations for telcos, privacy penalties with real teeth. Which world are you in?",
    sg: "Singapore expects fast answers: MAS clocks are measured in hours, and new resilience rules are landing for digital infrastructure. Which world are you in?",
    nz: "New Zealand's regulators are asking harder questions: cyber resilience self-assessments, incident reporting, 111 continuity. Which world are you in?",
    hk: "Hong Kong's rules just tightened: operational-resilience deadlines for banks, 12-hour incident reporting for critical infrastructure under Cap. 653. Which world are you in?",
    my: "Malaysia's clocks are short: six hours for critical-sector incident particulars, 72 hours for data breaches, and BNM's RMiT for financial services. Which world are you in?",
    th: "Thailand runs real clocks: 24 hours for critical-infrastructure incidents, 72 hours for data breaches, and BOT guidelines for financial services. Which world are you in?",
    kr: "Korea's bar is rising: the AI Basic Act is in force, PIPA breaches run on a 72-hour clock, and e-finance incidents have their own regime. Which world are you in?",
    other:
      "Different markets, same test: when things break, how fast do you know, respond and recover? Which world are you in?",
  };

  var OVERLAYS = {
    telco: [
      {
        id: "T1",
        sheetId: 21,
        domain: "svc",
        tier: "answers",
        text: "A network outage is affecting emergency call delivery. Walk through your first 30 minutes.",
        anchors: [
          "We would learn of emergency-call impact from *external reports*.",
          "The outage would be detected, but assessing emergency-call impact would *take time*.",
          "Emergency-call impact is flagged in monitoring; response steps are documented but *not rehearsed*.",
          "Automated detection flags emergency-call impact, a *rehearsed playbook* {ECS_PLAYBOOK}, and every step is timestamped.",
        ],
      },
      {
        id: "T2",
        sheetId: 22,
        domain: "svc",
        tier: "answers",
        text: "A major outage hits {MAJOR_OUTAGE}. Could you meet your customer-communication obligations while running the response?",
        anchors: [
          "Customer communication would be *improvised* and would compete with the response for the same people.",
          "Templates exist, but publishing *depends on the people running the response*.",
          "Communication roles are *separated from response roles*, though timeliness is not measured against the standard.",
          "Stakeholder updates run on a *rehearsed cadence* alongside the response, with timing evidenced against the standard.",
        ],
      },
      {
        id: "T3",
        sheetId: 23,
        domain: "ops",
        tier: "informs",
        text: dated(
          "2026-10-04",
          "Where are you against your chosen framework's maturity indicator 1, due 3 October 2026?",
          "Maturity indicator 1 fell due on 3 October 2026 - where did you land against your chosen framework?",
        ),
        anchors: [
          "We have *not yet mapped* ourselves against the chosen framework.",
          "A *gap assessment is underway*; remediation is not yet scheduled.",
          "Gaps are known and *remediation is planned* against the October date.",
          "MI1 requirements are *met and evidenced*; MI2 planning has started.",
        ],
      },
    ],
    ecom: [
      {
        id: "E1",
        sheetId: 24,
        domain: "peak",
        tier: "answers",
        text: "It's your biggest trading hour of the year and checkout error rates spike. What happens in the first 15 minutes?",
        anchors: [
          "We would likely hear from *customers or the operations floor* before our alerts.",
          "Alerts would fire, but triage and escalation depend on *who is around*.",
          "Peak-period alerting is tuned and an escalation path exists, though it is *not rehearsed at peak load*.",
          "Detection is tuned for peak, escalation is rehearsed, and a *war-room playbook* starts within minutes.",
        ],
      },
      {
        id: "E2",
        sheetId: 25,
        domain: "peak",
        tier: "answers",
        text: "Do you know your revenue cost per minute of checkout downtime - and is there an agreed threshold that triggers escalation?",
        anchors: [
          "Downtime cost has *not been quantified*.",
          "A figure exists in a business case, but it does *not drive operations*.",
          "The cost is known and informs prioritisation, but *nothing escalates automatically*.",
          "Cost per minute is agreed and monitored, and breaching the threshold *escalates automatically*.",
        ],
      },
      {
        id: "E3",
        sheetId: 26,
        domain: "peak",
        tier: "supports",
        text: "A payment provider degrades during peak. How quickly do you know it's them and not you?",
        anchors: [
          "We would spend the first hour *ruling out our own systems*.",
          "Provider status pages are checked *manually* once suspicion arises.",
          "Provider health is monitored, but correlating it with our checkout errors is *manual*.",
          "Provider signals are *correlated with our own telemetry*, so third-party fault is identified in minutes and the vendor playbook starts.",
        ],
      },
    ],
    digital: [
      {
        id: "D1",
        sheetId: 27,
        domain: "cust",
        tier: "answers",
        text: "An enterprise customer's regulator requires them to evidence YOUR incident response. Could you produce it?",
        anchors: [
          "Assembling it would be *a project* across tickets, chat logs and memory.",
          "Records exist in several systems; consolidation would *take days*.",
          "Incident records are structured, though customer-facing extracts need *manual work*.",
          "*Exportable per customer*, with timestamps and named responders, ready without a scramble.",
        ],
      },
      {
        id: "D2",
        sheetId: 28,
        domain: "cust",
        tier: "answers",
        text: "During a multi-tenant incident, how do you run customer-facing communications alongside the response?",
        anchors: [
          "Communications are *drafted from scratch* during the incident.",
          "A status page exists, but updates *lag the response* and customer messaging is improvised.",
          "Status page and templates exist with assigned owners, though proactive notification is *inconsistent*.",
          "Status page, proactive customer notifications and executive updates run on a *rehearsed cadence*, integrated with the response.",
        ],
      },
      {
        id: "D3",
        sheetId: 29,
        domain: "cust",
        tier: "informs",
        text: "Do you have an inventory of the regulatory obligations flowing down through your enterprise contracts?",
        anchors: [
          "We have *not mapped* the obligations inside enterprise contracts.",
          "Known for the *largest accounts only*, and held by the account team.",
          "An inventory exists but is *not maintained* as contracts renew.",
          "A *maintained inventory with owners*, reviewed at renewal, and tied to incident and evidence processes.",
        ],
      },
    ],
  };

  var CORE_ORDER = [
    "S3",
    "S6",
    "S5",
    "S9",
    "S1",
    "S12",
    "S4",
    "S10",
    "S2",
    "S11",
    "S7",
    "S8",
  ];

  CONFIRM_BY_CELL["au-digital-lite"] = {
    sub: "Use your report to identify gaps and plan next steps across the SOCI Act's data-storage and processing obligations, the Notifiable Data Breaches scheme and AI governance.",
    tiles: [
      {
        title: "What AI changes about incident response",
        desc: "AI is already inside claims, payments and fraud workflows. What gets asked when one misbehaves, and what your operating record needs to show.",
        url: "https://utm.io/ai-blog",
        cta: "Read the article",
      },
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "The state of service reliability in Australia",
        desc: "How Australian organisations are performing on reliability, and where the leaders are pulling ahead.",
        url: "https://www.pagerduty.com/resources/ai/whitepaper/state-of-service-reliability-australia/",
        cta: "Download report",
      },
    ],
    contact: CONFIRM_BY_CELL["au-digital"].contact,
  };
  CONFIRM_BY_CELL["other-fsi"] = {
    sub: "Use your report to identify gaps and plan next steps across operational-resilience regimes such as DORA and the UK's PS21/3, data-protection law and AI governance.",
    tiles: [
      {
        title: "Managing operational risk for banks",
        desc: "A practical look at reducing operational risk across financial services.",
        url: "https://www.pagerduty.com/resources/financial-services/learn/mitigate-operational-risk-banks/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/industries/financial-services/",
    },
  };
  CONFIRM_BY_CELL["other-telco"] = {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, critical-infrastructure incident reporting, data-protection law and AI governance.",
    tiles: [
      {
        title:
          "Operational resilience in the cloud: from reactive to proactive in the AI era",
        desc: "How network and platform operators are getting ahead of failure instead of chasing it.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/operational-resilience-in-the-cloud-from-reactive-to-proactive-in-the-ai-era/",
        cta: "Read the guide",
      },
      {
        title: "IDC report: building resilient IT operations",
        desc: "Proactive strategies to minimise downtime and enhance business continuity.",
        url: "https://www.pagerduty.com/resources/analyst-reports/analyst-report/idc-report-building-resilient-it-operations-proactive-strategies-to-minimize-downtime-and-enhance-business-continuity/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "From risk to operational resilience",
      desc: "Speak with our team about where PagerDuty fits in your response, and what other {PEER_NOUN} are doing about the same gaps.",
      url: "https://www.pagerduty.com/solutions/digital-resilience/",
    },
  };
  CONFIRM_BY_CELL["other-ecom"] = {
    sub: "Use your report to identify gaps and plan next steps across always-on customer experience, data-protection law, PCI DSS and AI governance.",
    tiles: [
      {
        title: "Protecting customer experience in the face of IT disruption",
        desc: "Keep checkout, fulfilment and support running when systems misbehave - especially at peak.",
        url: "https://www.pagerduty.com/resources/incident-management-response/ebook/ebook-protecting-customer-experience-in-the-face-of-it-disruption/",
        cta: "Read the guide",
      },
      {
        title: "Real-time digital operations for retail & eCommerce",
        desc: "How leading retailers run always-on digital operations through their biggest trading moments.",
        url: "https://www.pagerduty.com/resources/digital-transformation/webinar/real-time-digital-ops-for-retail-ecommerce/",
        cta: "Watch the webinar",
      },
    ],
    contact: {
      title: "Keep customer experience always-on",
      desc: "Speak with our team about keeping revenue-critical journeys running, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/industries/retail/",
    },
  };
  CONFIRM_BY_CELL["other-digital"] = {
    sub: "Use your report to identify gaps and plan next steps across operational resilience, data-protection law, cyber-incident reporting and AI governance.",
    tiles: [
      {
        title: "Reliability as a competitive advantage",
        desc: "Why the platforms your enterprise customers trust are the ones that treat reliability as a product feature.",
        url: "https://www.pagerduty.com/resources/digital-operations/ebook/reliability-as-a-competitive-advantage/",
        cta: "Read the guide",
      },
      {
        title: "GigaOm Radar for AIOps",
        desc: "An independent view of the AIOps landscape and where PagerDuty sits in it.",
        url: "https://www.pagerduty.com/resources/aiops/analyst-report/gigaom-aiops-radar-2025/",
        cta: "Download report",
      },
    ],
    contact: {
      title: "Scale resilient, AI-ready operations",
      desc: "Speak with our team about showing enterprise and regulated customers how you respond, and what other {PEER_NOUN} are doing.",
      url: "https://www.pagerduty.com/solutions/scaled-service-ownership/",
    },
  };

  var PDF_META = {
    default: {
      title: "Operational Readiness Assessment",
      filename: "Operational-Readiness-Assessment.pdf",
      linkLabel: "pagerduty.com",
      url: "https://www.pagerduty.com/",
    },
    au: {
      title: "Operational Readiness Assessment",
      filename: "Operational-Readiness-Assessment.pdf",
      linkLabel: "pagerduty.com/apra",
      url: "https://www.pagerduty.com/apra/",
    },
  };

  var appState = {
    route: null,
    industry: null,
    geo: null,
    cell: null,
    degraded: false,
    phase: null,
    optionalQs: [],
    runDomainOrder: [],
    domainTier: {},
  };
  var _domainCache = {};

  function resolveCell(industry, geo) {
    appState.industry = industry;
    appState.geo = geo;
    appState.degraded = false;
    _domainCache = {};
    var key = geo ? geo + "-" + industry : "default";
    if (industry === "generic") key = geo ? geo + "-generic" : "default";
    if (!CELLS[key]) {
      key = geo ? geo + "-generic" : "default";
      appState.degraded = true;
    }
    if (!CELLS[key]) {
      appState.geo = "other";
      key = CELLS["other-" + industry] ? "other-" + industry : "other-generic";
      appState.degraded = true;
    }
    if (CELLS[key] && !CELLS[key].built) {
      key = CELLS[key].geo + "-generic";
      appState.degraded = true;
    }
    appState.cell = key;
  }

  function getTier(pct) {
    for (var i = 0; i < readinessTiers.length; i++) {
      if (pct >= readinessTiers[i].min && pct <= readinessTiers[i].max)
        return readinessTiers[i];
    }
    return readinessTiers[0];
  }

  function getDomainVerdict(pct) {
    if (pct <= 25) return "Not ready";
    if (pct <= 50) return "Gaps to close";
    if (pct <= 75) return "On track";
    return "Strong";
  }

  function getDomainColour(pct) {
    if (pct <= 25) return "#DC2626";
    if (pct <= 50) return "#F59E0B";
    if (pct <= 75) return "#048A24";
    return "#048A24";
  }

  function esc(str) {
    var d = document.createElement("div");
    d.appendChild(document.createTextNode(str));
    return d.innerHTML;
  }

  var GEO_NAMES = {
    au: "Australia",
    sg: "Singapore",
    nz: "New Zealand",
    other: "Global",
  };
  var GEO_FLAGS = {
    au: "\uD83C\uDDE6\uD83C\uDDFA",
    sg: "\uD83C\uDDF8\uD83C\uDDEC",
    nz: "\uD83C\uDDF3\uD83C\uDDFF",
    other: "\uD83C\uDF0F",
  };

  function renderTailoredIntro() {
    var axes = homeAxes();
    var h = '<div class="tailored-picks">';
    h +=
      '<span class="tailored-pick">' +
      (GEO_FLAGS[appState.geo] || "") +
      " " +
      esc(GEO_NAMES[appState.geo] || "") +
      "</span>";
    h +=
      '<span class="tailored-pick">' +
      esc(INDUSTRY_NAMES[appState.industry] || "Cross-industry") +
      "</span>";
    if (apraClass() === "APRA")
      h +=
        '<span class="tailored-pick tailored-pick-apra">APRA-relevant</span>';
    h += "</div>";
    h +=
      '<div class="radar-label" style="margin-top:14px">Based on that, we\'ll measure you across</div>';
    h += '<div class="tailored-domains">';
    for (var i = 0; i < axes.length; i++) {
      var d = getDomain(axes[i]);
      h +=
        '<div class="tailored-domain"><span class="domain-name">' +
        esc(d.name) +
        '</span><span class="domain-sub">' +
        esc(d.subtitle) +
        "</span></div>";
    }
    h += "</div>";
    h +=
      '<p class="sample-caption" style="text-align:left;font-style:normal">Scenario questions, scored 1-4. Your results map each of these to what good looks like in your market.</p>';
    document.getElementById("sample-inner").innerHTML = h;
  }

  function fill(text) {
    var s = SLOTS[appState.cell] || SLOTS["default"];
    var out = text.replace(/\{(\w+)\}/g, function (m, k) {
      return s[k] !== null && s[k] !== undefined ? s[k] : m;
    });
    return out.replace(/s's\b/g, "s'");
  }

  function questionTextFor(q) {
    if (q.id === "S6") {
      var s = SLOTS[appState.cell] || SLOTS["default"];
      if (s.CYBER_AGENCY)
        return (
          "Could you report a critical incident to " +
          s.CYBER_AGENCY +
          " within " +
          s.CYBER_CLOCK +
          " - including at 2 AM on a long weekend?"
        );
      return "Could you meet your notification obligations at 2 AM on a long weekend?";
    }
    if (q.id === "S10" && CELLS[appState.cell].generic)
      return "If a regulator, auditor or enterprise customer requested evidence of your last 10 critical incidents, could you provide it?";
    return fill(q.text);
  }

  function materialiseQuestion(q) {
    return {
      id: q.id,
      sheetId: q.sheetId,
      domain: q.domain,
      tier: q.tier,
      question: questionTextFor(q),
      options: q.anchors.map(function (a, i) {
        var marked = fill(a);
        return {
          display: marked,
          text: marked.replace(/\*/g, ""),
          score: i + 1,
        };
      }),
    };
  }

  function optionHTML(option) {
    var body = esc(option.display || option.text).replace(
      /\*([^*]+)\*/g,
      "<strong>$1</strong>",
    );
    return (
      "<strong>" + MATURITY_LABELS[option.score - 1] + "</strong> - " + body
    );
  }

  function buildQuestions() {
    var cell = CELLS[appState.cell];
    var overlayQs = cell.overlay
      ? OVERLAYS[cell.overlay].filter(function (q) {
          return !(
            cell.overlayExclude && cell.overlayExclude.indexOf(q.id) >= 0
          );
        })
      : [];
    var hooks = overlayQs.filter(function (q) {
      return q.tier !== "informs";
    });
    var byId = {};
    SPINE.forEach(function (q) {
      byId[q.id] = q;
    });
    var order = CORE_ORDER.filter(function (id) {
      return !(cell.generic && (id === "S4" || id === "S6"));
    });
    var core = hooks.concat([byId["AI2"]]).concat(
      order.map(function (id) {
        return byId[id];
      }),
    );
    var optional = overlayQs
      .filter(function (q) {
        return q.tier === "informs";
      })
      .concat([byId["AI1"], byId["AI3"]]);
    function remap(q) {
      if (cell.domainRemap && cell.domainRemap[q.id]) {
        var c = {};
        for (var k in q) c[k] = q[k];
        c.domain = cell.domainRemap[q.id];
        return c;
      }
      return q;
    }
    return { core: core.map(remap), optional: optional.map(remap) };
  }

  function getDomain(slug) {
    if (_domainCache[slug]) return _domainCache[slug];
    var geo = CELLS[appState.cell] ? CELLS[appState.cell].geo : null;
    var merged = {};
    [
      DOMAINS[slug],
      geo && DOMAIN_OVERRIDES[geo] && DOMAIN_OVERRIDES[geo][slug],
      DOMAIN_OVERRIDES[appState.cell] && DOMAIN_OVERRIDES[appState.cell][slug],
    ].forEach(function (layer) {
      if (layer) for (var k in layer) merged[k] = layer[k];
    });
    var out = {};
    for (var k in merged)
      out[k] = typeof merged[k] === "string" ? fill(merged[k]) : merged[k];
    _domainCache[slug] = out;
    return out;
  }

  function apraClass() {
    if (appState.cell === "au-fsi" || appState.cell === "au-digital")
      return "APRA";
    if (
      appState.cell === "au-fsi-lite" ||
      appState.cell === "sg-fsi" ||
      appState.cell === "nz-fsi" ||
      appState.cell === "hk-fsi" ||
      appState.cell === "my-fsi" ||
      appState.cell === "th-fsi" ||
      appState.cell === "kr-fsi"
    )
      return "FSI";
    return "NONE";
  }

  function cellProps(extra) {
    var base = {
      assessment: ASSESSMENT_NAME,
      industry: appState.industry || "",
      geo: appState.geo || "",
      cell: appState.cell || "",
      apra: apraClass(),
      degraded: appState.degraded,
    };
    if (extra) for (var k in extra) base[k] = extra[k];
    return base;
  }

  function buildRadarSVG(scores, axesOpt) {
    var axes = axesOpt || appState.runDomainOrder;
    var size = 230,
      padding = 55,
      full = size + padding * 2;
    var cx = full / 2,
      cy = full / 2,
      r = size * 0.44;
    var levels = [25, 50, 75, 100];
    var step = (2 * Math.PI) / axes.length,
      start = -Math.PI / 2;
    var lm = axes.length >= 5 ? 1.22 : 1.28;

    function pt(i, v) {
      var a = start + i * step,
        d = (v / 100) * r;
      return { x: cx + d * Math.cos(a), y: cy + d * Math.sin(a) };
    }

    var svg =
      '<svg viewBox="0 0 ' +
      full +
      " " +
      full +
      '" width="100%" style="max-width:' +
      full +
      'px;overflow:visible">';

    for (var l = 0; l < levels.length; l++) {
      var pts = [];
      for (var i = 0; i < axes.length; i++) {
        var p = pt(i, levels[l]);
        pts.push(p.x + "," + p.y);
      }
      svg +=
        '<polygon points="' +
        pts.join(" ") +
        '" fill="none" stroke="#E5E7EB" stroke-width="' +
        (l === levels.length - 1 ? "1" : "0.5") +
        '" opacity="0.8"/>';
    }
    for (var i = 0; i < axes.length; i++) {
      var p = pt(i, 100);
      svg +=
        '<line x1="' +
        cx +
        '" y1="' +
        cy +
        '" x2="' +
        p.x +
        '" y2="' +
        p.y +
        '" stroke="#E5E7EB" stroke-width="0.5"/>';
    }

    var dpts = [];
    for (var i = 0; i < axes.length; i++)
      dpts.push(pt(i, scores[axes[i]] || 0));
    svg +=
      '<polygon points="' +
      dpts
        .map(function (p) {
          return p.x + "," + p.y;
        })
        .join(" ") +
      '" fill="rgba(6,172,56,0.15)" stroke="#048A24" stroke-width="2"/>';

    for (var i = 0; i < dpts.length; i++) {
      var col = getDomainColour(scores[axes[i]] || 0);
      svg +=
        '<circle cx="' +
        dpts[i].x +
        '" cy="' +
        dpts[i].y +
        '" r="4" fill="' +
        col +
        '" stroke="#FFF" stroke-width="2"/>';
    }

    var ff =
      'font-family="Plain,-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif"';
    for (var i = 0; i < axes.length; i++) {
      var k = axes[i],
        a = start + i * step;
      var lx = cx + r * lm * Math.cos(a),
        ly = cy + r * lm * Math.sin(a);
      var cosA = Math.cos(a);
      var anc = Math.abs(cosA) < 0.1 ? "middle" : cosA > 0 ? "start" : "end";
      if (anc === "start") lx -= 7;
      var d = getDomain(k);
      var lines = d.radarLines || d.name.split(" ");
      svg +=
        "<g onclick=\"scrollToDomain('" + k + '\')" style="cursor:pointer">';
      svg +=
        '<text x="' +
        lx +
        '" y="' +
        (ly - 6) +
        '" text-anchor="' +
        anc +
        '" fill="#191919" font-size="13" font-weight="700" ' +
        ff +
        ">" +
        esc(lines[0]) +
        "</text>";
      if (lines.length > 1) {
        svg +=
          '<text x="' +
          lx +
          '" y="' +
          (ly + 10) +
          '" text-anchor="' +
          anc +
          '" fill="#191919" font-size="13" font-weight="700" ' +
          ff +
          ">" +
          esc(lines.slice(1).join(" ")) +
          "</text>";
      }
      svg += "</g>";
    }
    svg += "</svg>";
    return svg;
  }

  function scrollToDomain(key) {
    var card = document.getElementById("dc-" + key);
    if (!card) return;
    var detail = document.getElementById("dd-" + key);
    if (detail && !detail.classList.contains("open")) toggleDomain(key);
    setTimeout(function () {
      card.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 60);
  }

  function resolveCellFromParams(country, industry, apra) {
    appState.route = "link";
    if (industry === "fsi" && country === "au") {
      resolveCell("fsi", "au");
      appState.cell = apra === "yes" ? "au-fsi" : "au-fsi-lite";
      _domainCache = {};
      return;
    }
    if (industry === "digital" && country === "au") {
      resolveCell("digital", "au");
      appState.cell = apra === "yes" ? "au-digital" : "au-digital-lite";
      _domainCache = {};
      return;
    }
    resolveCell(industry || "other", country || "other");
  }

  // Links from the first quiz: ?q1=1.q2=2...q13=4. Thirteen answers by position,
  // no country/industry. Positions map to today's sheet ids; the old "during a
  // major incident" (4) and "automated response" (9) questions became sheet 20.
  var LEGACY_SHEET_IDS = [1, 2, 3, 20, 13, 15, 4, 5, 20, 6, 16, 8, 9];

  function legacyResultsParams(search) {
    if (/[?&](country|geo|market)=/.test(search)) return null;
    if (!/^\?q1=\d\.q2=\d/.test(search)) return null;
    var byPos = {};
    search
      .slice(1)
      .split(/[.&]/)
      .forEach(function (pair) {
        var m = /^q(\d+)=(\d)$/.exec(pair);
        if (m) byPos[parseInt(m[1], 10)] = parseInt(m[2], 10);
      });
    var p = new URLSearchParams({
      country: "au",
      industry: "fsi",
      apra: "yes",
    });
    var merged = [];
    LEGACY_SHEET_IDS.forEach(function (sheetId, i) {
      var score = byPos[i + 1];
      if (!(score >= 1 && score <= 4)) return;
      if (sheetId === 20) merged.push(score);
      else p.set("q" + sheetId, String(score));
    });
    if (merged.length) {
      var sum = merged.reduce(function (a, b) {
        return a + b;
      }, 0);
      p.set("q20", String(Math.round(sum / merged.length)));
    }
    return p;
  }

  function render() {
    var p =
      legacyResultsParams(window.location.search) ||
      new URLSearchParams(window.location.search);
    var country = p.get("country") || p.get("geo") || p.get("market"),
      industry = p.get("industry") || p.get("sector") || p.get("vertical"),
      apra = p.get("apra");
    var scores = {};
    var any = false;
    p.forEach(function (v, k) {
      var m = /^q(\d+)$/.exec(k);
      if (m) {
        var s = parseInt(v, 10);
        if (s >= 1 && s <= 4) {
          scores[parseInt(m[1], 10)] = s;
          any = true;
        }
      }
    });
    if (!country || !industry || !any) {
      document.getElementById("results-container").innerHTML =
        '<div class="error-card"><div class="error-title">No results to display</div>' +
        '<div class="error-desc">This page requires a valid results link, e.g.<br>/apra/assessment-quiz/results?country=au&amp;industry=fsi&amp;apra=yes&amp;q1=4&amp;q18=2...</div></div>';
      return;
    }
    resolveCellFromParams(country, industry, apra);

    var built = buildQuestions();
    var allQs = built.core
      .concat(built.optional)
      .map(materialiseQuestion)
      .filter(function (q) {
        return scores[q.sheetId];
      });
    appState.runDomainOrder = DOMAIN_CANON.filter(function (slug) {
      return allQs.some(function (q) {
        return q.domain === slug;
      });
    });
    var ds = {};
    appState.runDomainOrder.forEach(function (k) {
      var qs = allQs.filter(function (q) {
        return q.domain === k;
      });
      var sum = 0;
      qs.forEach(function (q) {
        sum += scores[q.sheetId];
      });
      ds[k] = Math.round((sum / (qs.length * 4)) * 100);
    });
    var qSum = 0,
      qN = 0;
    allQs.forEach(function (q) {
      qSum += scores[q.sheetId];
      qN++;
    });
    var overall = qN ? Math.round((qSum / (qN * 4)) * 100) : 0;
    var tier = getTier(overall);
    var sorted = appState.runDomainOrder.slice().sort(function (a, b) {
      return ds[a] - ds[b];
    });

    var geoNames = {
      au: "Australia",
      sg: "Singapore",
      nz: "New Zealand",
      hk: "Hong Kong",
      my: "Malaysia",
      th: "Thailand",
      kr: "South Korea",
      other: "Global",
    };
    var indNames = {
      fsi: "Financial services",
      telco: "Telecommunications",
      ecom: "Retail & ecommerce",
      digital: "Technology & digital",
      generic: "Cross-industry",
      other: "Cross-industry",
    };

    var aiPulse =
      allQs.filter(function (q) {
        return q.domain === "ai";
      }).length === 1;

    var h = "";
    h +=
      '<div class="results-meta"><strong>' +
      esc(geoNames[country] || country) +
      "</strong> &middot; <strong>" +
      esc(indNames[industry] || industry) +
      "</strong>" +
      (apraClass() === "APRA"
        ? " &middot; <strong>APRA-relevant</strong>"
        : "") +
      "</div>";

    h +=
      '<div class="card"><div class="score-header"><div><div class="score-label">Overall readiness</div>';
    h +=
      '<span class="score-big" style="color:' +
      tier.colour +
      '">' +
      overall +
      "%</span></div>";
    h +=
      '<span class="tier-badge" style="background:' +
      tier.bg +
      ";color:" +
      tier.colour +
      '">' +
      esc(tier.label) +
      "</span></div>";
    h +=
      '<div class="score-bar"><div class="score-bar-fill" style="width:' +
      overall +
      "%;background:" +
      tier.colour +
      '"></div></div>';
    h += '<div class="score-desc">' + esc(tier.description) + "</div>";
    h +=
      '<div class="score-basis">Based on the ' +
      allQs.length +
      " questions answered" +
      (aiPulse ? " - the optional AI deep-dive wasn't included." : ".") +
      "</div></div>";

    var weakDomains = sorted.filter(function (k) {
      return ds[k] < 76 && !(k === "ai" && aiPulse);
    });
    h +=
      '<div class="priority-box"><div class="priority-title">Priority areas</div>';
    if (weakDomains.length === 0) {
      if (aiPulse && ds.ai < 75) {
        h +=
          '<div class="priority-text">No urgent gaps across the core domains. The <strong>AI readiness</strong> reading is a quick pulse from a single scenario - worth a closer look, but not a verdict.</div>';
      } else {
        h +=
          '<div class="priority-text">No major gaps were identified from these responses. Review the detail below before the next audit or regulatory review.</div>';
      }
    } else {
      h +=
        '<div class="priority-text">Greatest exposure is in <strong>' +
        esc(getDomain(weakDomains[0]).name) +
        "</strong>";
      if (weakDomains.length > 1)
        h +=
          " and <strong>" + esc(getDomain(weakDomains[1]).name) + "</strong>";
      h +=
        ". " +
        (weakDomains.length === 1 ? "This should be" : "These should be") +
        " the immediate focus.</div>";
    }
    h += "</div>";

    h += '<div class="results-columns">';
    h +=
      '<div class="results-col-left"><div class="card"><div class="radar-label">Readiness by domain</div>';
    h += '<div class="radar-wrap">' + buildRadarSVG(ds) + "</div></div></div>";
    h += '<div class="results-col-right">';
    h +=
      '<div class="radar-label mobile-domain-heading">Readiness by domain</div>';
    var isMobile = window.innerWidth <= 768;
    var firstExpanded = false;
    for (var i = 0; i < sorted.length; i++) {
      var k = sorted[i],
        pct = ds[k],
        col = getDomainColour(pct),
        v = getDomainVerdict(pct),
        d = getDomain(k);
      var autoExpand = !firstExpanded && !isMobile;
      if (autoExpand) {
        firstExpanded = true;
        expandedDomain = k;
      }
      h +=
        '<div class="domain-card' +
        (autoExpand ? " expanded" : "") +
        '" id="dc-' +
        k +
        '"><button class="domain-header" onclick="toggleDomain(\'' +
        k +
        "')\">";
      h +=
        '<div class="domain-header-left"><div class="domain-name-row"><span class="domain-name">' +
        esc(d.name) +
        '</span><span class="domain-sub">' +
        esc(d.subtitle) +
        (k === "ai" && aiPulse
          ? " &middot; quick pulse from one scenario"
          : "") +
        "</span></div>";
      h +=
        '<div class="domain-bar-row"><div class="domain-bar"><div class="domain-bar-fill" style="width:' +
        pct +
        "%;background:" +
        col +
        '"></div></div>';
      h +=
        '<span class="domain-pct" style="color:' +
        col +
        '">' +
        pct +
        "%</span></div></div>";
      h +=
        '<div class="domain-header-right"><span class="verdict-badge" style="background:' +
        col +
        "15;color:" +
        col +
        '">' +
        esc(v) +
        "</span>";
      h +=
        '<svg class="chevron' +
        (autoExpand ? " open" : "") +
        '" id="chev-' +
        k +
        '" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#6B7280" stroke-width="1.5"><path d="M4 6l4 4 4-4"/></svg></div>';
      h +=
        '</button><div class="domain-detail' +
        (autoExpand ? " open" : "") +
        '" id="dd-' +
        k +
        '"><div class="domain-detail-inner">';
      h +=
        '<div class="detail-section"><div class="detail-label detail-label-red">Why it matters</div><div class="detail-text">' +
        esc(d.regRisk) +
        "</div></div>";
      if (d.resource) {
        h +=
          '<div class="detail-section"><a class="resource-link" href="' +
          esc(d.resource.url) +
          '" target="_blank" rel="noopener noreferrer">' +
          esc(d.resource.label) +
          "</a></div>";
      }
      h +=
        '<div class="detail-section"><div class="detail-label detail-label-dark">What good looks like</div><div class="detail-text">' +
        esc(d.whatGoodLooksLike) +
        "</div></div>";
      h += "</div></div></div>";
    }
    h += "</div></div>";

    h += '<div class="qa-section">';
    h += '<div class="qa-section-title">Question &amp; answer breakdown</div>';
    for (var di = 0; di < appState.runDomainOrder.length; di++) {
      var dk = appState.runDomainOrder[di];
      var domainQs = allQs.filter(function (q) {
        return q.domain === dk;
      });
      var hasGaps = domainQs.some(function (q) {
        return scores[q.sheetId] < 4;
      });
      h += '<div class="qa-domain-group">';
      h +=
        '<div class="qa-domain-label">' +
        esc(getDomain(dk).name) +
        " &mdash; " +
        esc(getDomain(dk).subtitle) +
        "</div>";
      for (var j = 0; j < domainQs.length; j++) {
        var q = domainQs[j];
        var sc = scores[q.sheetId];
        var opt = q.options[sc - 1];
        var qcol = getDomainColour(sc * 25);
        h += '<div class="qa-item' + (sc === 4 ? " qa-perfect" : "") + '">';
        h +=
          '<div class="qa-question">' +
          esc(q.question) +
          ' <span class="qa-score" style="color:' +
          qcol +
          '">' +
          sc +
          "/4</span></div>";
        h += '<div class="qa-answer">' + esc(opt.text) + "</div>";
        h += "</div>";
      }
      var hasPerfect = domainQs.some(function (q) {
        return scores[q.sheetId] === 4;
      });
      if (hasPerfect) {
        var label = hasGaps ? "Show questions scored 4/4" : "Show all answers";
        h +=
          '<button class="qa-toggle" onclick="togglePerfect(this)">' +
          label +
          "</button>";
      }
      h += "</div>";
    }
    h += "</div>";

    h += '<p class="disclaimer">' + esc(DISCLAIMER) + "</p>";

    document.getElementById("results-container").innerHTML = h;

    var viewedProps = {
      event_type: "assessment_results_viewed",
      overall_score: overall,
      tier: tier.label,
    };
    appState.runDomainOrder.forEach(function (k) {
      viewedProps[k + "_score"] = ds[k];
    });
    segTrack("Generic Event", cellProps(viewedProps));
  }

  var expandedDomain = null;

  function toggleDomain(key) {
    var card = document.getElementById("dc-" + key);
    var detail = document.getElementById("dd-" + key);
    var chev = document.getElementById("chev-" + key);
    var isOpen = detail.classList.contains("open");
    if (expandedDomain && expandedDomain !== key) {
      var pd = document.getElementById("dd-" + expandedDomain);
      var pc = document.getElementById("dc-" + expandedDomain);
      var pv = document.getElementById("chev-" + expandedDomain);
      if (pd) pd.classList.remove("open");
      if (pc) pc.classList.remove("expanded");
      if (pv) pv.classList.remove("open");
    }
    if (isOpen) {
      detail.classList.remove("open");
      card.classList.remove("expanded");
      chev.classList.remove("open");
      expandedDomain = null;
    } else {
      detail.classList.add("open");
      card.classList.add("expanded");
      chev.classList.add("open");
      expandedDomain = key;
    }
  }

  function togglePerfect(btn) {
    var group = btn.parentElement;
    var showing = group.classList.toggle("show-all");
    if (showing) btn.textContent = btn.textContent.replace(/^Show /, "Hide ");
    else btn.textContent = btn.textContent.replace(/^Hide /, "Show ");
  }

  window.toggleDomain = toggleDomain;
  window.togglePerfect = togglePerfect;
  window.scrollToDomain = scrollToDomain;
  document.addEventListener("DOMContentLoaded", render);
})();
